import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);

  const next = new URLSearchParams(loc.search).get("next") || "/dashboard";

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      nav(next);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign in failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <Header />
      <section className="px-6 sm:px-12 lg:px-20 py-20 max-w-md mx-auto">
        <p className="label-eyebrow mb-4">Return to Badi</p>
        <h1 className="font-serif text-4xl text-[#2D322B] mb-10">Sign in</h1>
        <form onSubmit={onSubmit} className="space-y-6" data-testid="login-form">
          <div>
            <label className="label-eyebrow block mb-2">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email"
              className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2 outline-none transition-colors text-[#2D322B]"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label-eyebrow">Password</label>
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                data-testid="login-toggle-password"
                className="text-[10px] uppercase tracking-widest text-[#5C6359] hover:text-[#C05C42] inline-flex items-center gap-1 transition-colors"
              >
                {showPassword ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            <input
              type={showPassword ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password"
              className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2 outline-none transition-colors text-[#2D322B]"
            />
            <div className="mt-2 text-right">
              <Link to="/forgot-password" data-testid="login-forgot-link" className="text-[10px] uppercase tracking-widest text-[#5C6359] hover:text-[#C05C42] transition-colors">
                Forgot password?
              </Link>
            </div>
          </div>
          <button type="submit" disabled={busy} data-testid="login-submit" className="btn-primary w-full">
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <p className="mt-8 text-sm text-[#5C6359]">
          New to Badi?{" "}
          <Link to="/signup" className="text-[#C05C42] hover:underline" data-testid="login-link-signup">Begin here</Link>
        </p>
      </section>
      <Footer />
    </div>
  );
}
